import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';

/**
 * FAQ Section — SEO local powerhouse.
 * Each question targets a real Google search ("quanto custa filmagem em foz...",
 * "agência de marketing foz do iguaçu", etc.) and renders as Schema.org FAQPage
 * JSON-LD for rich snippets.
 */
const faqItems = [
  {
    q: 'A Coutin Agência atende em Foz do Iguaçu e região?',
    a: 'Sim. A Coutin Agência é sediada em Foz do Iguaçu, PR, e atende toda a região da Tríplice Fronteira — incluindo Santa Terezinha de Itaipu, Medianeira, São Miguel do Iguaçu, Cascavel e Ciudad del Este. Para projetos especiais, atendemos em todo o Paraná e estados vizinhos.',
  },
  {
    q: 'Quanto custa uma filmagem profissional em Foz do Iguaçu?',
    a: 'O investimento varia conforme o porte do projeto: vídeos institucionais para empresas começam a partir de R$ 895, enquanto produções completas com drone FPV, equipe e edição cinematográfica podem chegar a R$ 3.795 ou mais. Solicite um orçamento personalizado pelo WhatsApp (45) 98806-8508.',
  },
  {
    q: 'Vocês fazem sites para empresas em Foz do Iguaçu?',
    a: 'Sim. Criamos sites de alta conversão para empresas locais — institucionais, landing pages, lojas virtuais e portais corporativos. Todos otimizados para Google (SEO), responsivos e integrados com WhatsApp, Google Analytics e ferramentas de tráfego pago.',
  },
  {
    q: 'Qual é o diferencial da Coutin Agência em relação a outras agências?',
    a: 'Trabalhamos com equipamentos Sony profissionais, drones DJI Avata FPV (poucas agências da região têm essa tecnologia), gravação em 4K Ultra HD e slow motion 120fps. Somos especialistas em produções cinematográficas — não apenas "vídeos de celular" — entregando padrão broadcast para empresas locais.',
  },
  {
    q: 'Vocês também fazem gestão de redes sociais?',
    a: 'Sim. Oferecemos pacotes mensais de gestão de Instagram, Facebook e TikTok, incluindo planejamento estratégico, produção de conteúdo (vídeos e fotos), copywriting, agendamento e relatórios. Combinamos produção audiovisual + estratégia digital no mesmo lugar.',
  },
  {
    q: 'Vocês cobrem eventos, shows e casamentos em Foz do Iguaçu?',
    a: 'Sim. Cobrimos shows, eventos corporativos, congressos, casamentos, formaturas e ativações de marca. Entregamos vídeo principal cinematográfico + reels para redes sociais + fotos high-end, tudo com drone quando aplicável.',
  },
  {
    q: 'Em quanto tempo o vídeo fica pronto?',
    a: 'Reels e conteúdo curto: 3 a 7 dias úteis. Vídeos institucionais: 10 a 15 dias úteis. Produções complexas (séries, documentários, ads): cronograma personalizado discutido no briefing inicial.',
  },
  {
    q: 'Como solicitar um orçamento?',
    a: 'O caminho mais rápido é pelo WhatsApp (45) 98806-8508. Você também pode enviar um e-mail para agenciacoutin@gmail.com ou usar o formulário de contato do site. Respondemos em até 2 horas úteis.',
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <section
      id="faq"
      className="py-24 bg-[#0A0A0A] relative overflow-hidden border-t border-white/5"
      data-testid="faq-section"
    >
      {/* JSON-LD for rich snippets */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="w-8 h-[1px] bg-[#4ECDC4]"></div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#4ECDC4]">
              Dúvidas Frequentes
            </span>
            <div className="w-8 h-[1px] bg-[#4ECDC4]"></div>
          </div>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold text-[#F5F5F0] mb-4">
            Perguntas sobre <span className="text-[#4ECDC4]">Marketing e Audiovisual</span> em Foz do Iguaçu
          </h2>
          <p className="text-base text-[#A1A1AA] max-w-2xl mx-auto font-light">
            Reunimos as perguntas mais comuns que recebemos no WhatsApp. Não achou a sua? Fale conosco.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqItems.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
                className="border border-white/10 rounded-2xl bg-gradient-to-br from-[#161616] to-[#0E0E0E] overflow-hidden hover:border-[#4ECDC4]/40 transition-colors duration-300"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left"
                  data-testid={`faq-toggle-${index}`}
                  aria-expanded={isOpen}
                >
                  <span className="text-[#F5F5F0] font-semibold text-base md:text-lg leading-snug">
                    {item.q}
                  </span>
                  <motion.div
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="shrink-0 w-9 h-9 rounded-full bg-[#4ECDC4]/10 border border-[#4ECDC4]/30 flex items-center justify-center"
                  >
                    <Plus className="w-4 h-4 text-[#4ECDC4]" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-5 text-[#A1A1AA] text-sm md:text-base leading-relaxed font-light">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 text-center"
        >
          <a
            href="https://wa.me/5545988068508?text=Ol%C3%A1%21%20Vim%20pelo%20site%20da%20Coutin%20e%20gostaria%20de%20saber%20mais."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#4ECDC4] hover:text-[#F5F5F0] font-medium text-sm uppercase tracking-wider transition-colors duration-300"
            data-testid="faq-whatsapp-link"
          >
            Tem outra dúvida? Fale conosco no WhatsApp
            <span aria-hidden="true">→</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
